import React, { useState, useEffect, useCallback } from 'react';
import {
  LogOut, Shield, User, Eye, EyeOff,
  Plus, Pencil, Trash2, CheckCircle, XCircle,
  Calendar, Search, ChevronDown, X, Save, RefreshCw, AlertCircle
} from 'lucide-react';

// ============================================================
// CONFIG
// ============================================================
const LOCAL_HOST = ""; // "http://localhost:8002";      // this same machine (MSSQL local dev)
const LOCAL_LAN = ""; //"http://10.156.158.250:8002"; // old LAN dev box
const SERVER = ""; //"http://210.18.155.122:8002";
const _host = ""; //window.location.hostname;
let API_BASE = (
  _host === 'localhost' || _host === '127.0.0.1'
) ? LOCAL_HOST : (
  _host === '10.156.158.250' || _host === '192.168.10.193'
) ? LOCAL_LAN : _host.endsWith('kitbit.tech') ? '' : SERVER;   // vr.kitbit.tech: same-origin /api (as the live build)

// ---- Data source switch (Local DB / Server DB) ----
const SERVER_URL = _host.endsWith('kitbit.tech') ? '' : 'https://api.kitbit.tech';
const LOCAL_URL = (_host === 'localhost' || _host === '127.0.0.1') ? LOCAL_HOST : LOCAL_LAN;
const IS_HTTPS = window.location.protocol === 'https:';   // https page cannot call http local API (browser blocks it)
const readSrc = () => { try { return localStorage.getItem('kitbit_src'); } catch { return null; } };
if (readSrc() === 'server') API_BASE = SERVER_URL;
else if (readSrc() === 'local' && !IS_HTTPS) API_BASE = LOCAL_URL;
const setApiSource = (src) => {
  API_BASE = src === 'server' ? SERVER_URL : LOCAL_URL;
  try { localStorage.setItem('kitbit_src', src); } catch { }
};
const currentSource = () => (API_BASE === LOCAL_URL ? 'local' : 'server');

// ============================================================
// MODULE BEHAVIOUR CONFIG (mirrors Python MODULE_CONFIG)
// Dashboard reads hasAssessment / hasGuided from the API —
// this client-side map is ONLY needed for display tweaks that
// the API doesn't control (column labels, extra UI, etc.).
// To add a new module: add one entry here. Nothing else changes.
// ============================================================
const MODULE_UI_CONFIG = {
  // module_id : { scoreLabel, showWrongSteps, stepTimerVisible }
  2: { scoreLabel: null, showWrongSteps: false, stepTimerVisible: false },
  7: { scoreLabel: 'Score', showWrongSteps: true, stepTimerVisible: true, totalScoreMax: 100  },
  1: { scoreLabel: 'Score', showWrongSteps: true, stepTimerVisible: true, multiTask: true ,looseTaskMatch: true,   totalScoreMax: 100 }, // Engine Control Room (multi-task)
  5: { scoreLabel: 'Score', showWrongSteps: true, stepTimerVisible: true, multiTask: true, looseTaskMatch: true, totalScoreMax: 100  }, // Pyrotechnics (multi-task; task filter ignores capitals / leading number)
  18: { scoreLabel: 'Score', showWrongSteps: true, stepTimerVisible: true, multiTask: true , totalScoreMax: 100, partialScore: true }, // Welding (multi-task; partialScore = show +points even when step marked wrong)

  10: { scoreLabel: 'Score', showWrongSteps: true, stepTimerVisible: true, multiTask: true, totalScoreMax: 300 }, // MOP(multi-task)
  // Ro Ro Ship: only this module shows the combined 5-task / 500-point
  // SCORE card (Tour, MCQ, Dump Truck, Truck, Car all in one session).
  3: { scoreLabel: 'Score', showWrongSteps: true, stepTimerVisible: true, multiTask: true, totalScoreMax: 500 },
  11: { scoreLabel: 'Score', showWrongSteps: true, stepTimerVisible: true, multiTask: true, totalScoreMax: 700 },
   20: { scoreLabel: 'Score', showWrongSteps: true, stepTimerVisible: true, multiTask: true, totalScoreMax: 100 },//LNG desk
  // future modules follow the same shape — defaults used if absent
};
const DEFAULT_MODULE_UI = { scoreLabel: 'Score', showWrongSteps: false, stepTimerVisible: false, multiTask: false, totalScoreMax: null };
const getModuleUI = (id) => MODULE_UI_CONFIG[id] || DEFAULT_MODULE_UI;
// Task filter. Modules with looseTaskMatch (only Pyrotechnics, id 5) compare task
// names without capitals, extra spaces or a leading number ("4. Smoke Signal" =
// "smoke signal"). All other modules keep the exact match they had before.
const taskKey = (n) => (n || '').replace(/^\s*\d+\s*[.)-]\s*/, '').replace(/\s+/g, ' ').trim().toLowerCase();
const sameTask = (ui, a, b) => ui.looseTaskMatch ? taskKey(a) === taskKey(b) : a === b;

// ============================================================
// API HELPER
// ============================================================
const api = async (path, method = 'GET', body = null) => {
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: { 'Content-Type': 'application/json' },
    ...(body ? { body: JSON.stringify(body) } : {})
  });
  return await res.json();
};

// ── Specific error message from network/fetch failure ────────
const getNetworkError = (err) => {
  const msg = (err?.message || '').toLowerCase();
  if (msg.includes('failed to fetch') || msg.includes('networkerror') || msg.includes('err_connection_refused'))
    return `Cannot reach server. Check if the server is running on port .`;
  if (msg.includes('econnrefused'))
    return 'Server refused the connection — wrong port or server not started.';
  if (msg.includes('timeout') || msg.includes('timed out'))
    return 'Server took too long to respond — check your network connection.';
  if (msg.includes('err_name_not_resolved') || msg.includes('getaddrinfo'))
    return 'Server address not found — check the server IP or URL.';
  if (msg.includes('err_network_changed'))
    return 'Network changed during request — reconnect and try again.';
  return `Connection error — ${err?.message || 'unknown error'}. Check server is running on .`;
};

// ============================================================
// DESIGN TOKENS
// ============================================================
// ============================================================
// TITLE CASE HELPER - for displaying step names / task names with
// each word capitalized (e.g. "car lashing with loop" -> "Car Lashing
// With Loop"), WITHOUT changing the underlying stored data. Only used
// at render time in the dashboard. Only capitalizes the FIRST letter
// of each word - leaves the rest of each word untouched, so existing
// acronyms (e.g. "CCTV AFT", "PPE") aren't accidentally lowercased.
// ============================================================
const toTitleCase = (str) => {
  if (!str) return str;
  // First pass: capitalize the first letter of each whitespace-separated word.
  let result = str.replace(/\w\S*/g, (word) => word.charAt(0).toUpperCase() + word.slice(1));
  // Second pass: '/' isn't whitespace, so "decks/lashing" is treated as ONE
  // word above and only "D" gets capitalized. This capitalizes the letter
  // right after every '/' too, e.g. "Decks/lashing" -> "Decks/Lashing".
  result = result.replace(/\/(\w)/g, (_, c) => '/' + c.toUpperCase());
  return result;
};

const C = {
  bg: '#0A1628',
  surface: '#0D1B2F',
  card: 'rgba(30,41,59,0.85)',
  border: 'rgba(74,144,226,0.2)',
  borderH: 'rgba(74,144,226,0.5)',
  blue: '#3b82f6',
  green: '#10b981',
  red: '#ef4444',
  amber: '#f59e0b',
  purple: '#8b5cf6',
  muted: '#94a3b8',
  dim: '#64748b',
};

const ICON_OPTIONS = ['⚙️', '🏗️', '🧭', '📦', '🚨', '🔧', '🛠️', '🌊', '⛽', '🔬', '📡', '🛡️', '🔥', '💡', '🎯', ' 🚢 ', '⚓'];
const COLOR_OPTIONS = ['#3b82f6', '#f59e0b', '#10b981', '#8b5cf6', '#ef4444', '#06b6d4', '#f43f5e', '#84cc16', '#fb923c', '#a78bfa'];

// ============================================================
// KITBIT LOGO (embedded as base64 - no external file dependency)
// ============================================================
const KITBIT_LOGO = 'data:image/png;base64,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';

// ============================================================
// GLOBAL STYLES — covers iOS safari, Windows scroll, touch
// ============================================================
const GlobalStyles = () => (
  <style>{`
    *, *::before, *::after { box-sizing: border-box; -webkit-tap-highlight-color: transparent; }
    html { -webkit-text-size-adjust: 100%; }
    body { margin: 0; padding: 0; font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Segoe UI', sans-serif; background: #0A1628; overscroll-behavior: none; }
    @keyframes spin   { from { transform: rotate(0deg);   } to { transform: rotate(360deg); } }
    @keyframes fadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
    @keyframes slideUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }

    /* iOS Safari — prevent zoom on input focus */
    input, select, textarea { font-size: 16px !important; }

    /* Date picker */
    input[type="date"]::-webkit-calendar-picker-indicator { cursor: pointer; filter: invert(0.6); }
    input[type="date"]::-webkit-calendar-picker-indicator:hover { filter: invert(0.4) sepia(1) saturate(5) hue-rotate(200deg); }

    /* Scrollbar — desktop only */
    @media (hover: hover) {
      ::-webkit-scrollbar       { width: 5px; height: 5px; }
      ::-webkit-scrollbar-track { background: rgba(15,23,42,0.4); }
      ::-webkit-scrollbar-thumb { background: #3b82f6; border-radius: 3px; }
    }

    select option { background: #1e293b; color: #fff; }

    /* Touch targets — minimum 44px for Apple HIG */
    button { min-height: 36px; }

    /* Safe area insets for iPhone notch / home bar */
    .safe-bottom { padding-bottom: env(safe-area-inset-bottom, 0px); }
    .safe-top    { padding-top:    env(safe-area-inset-top,    0px); }

    /* Smooth scroll everywhere */
    * { -webkit-overflow-scrolling: touch; }

    /* Table horizontal scroll on mobile */
    .table-wrap { overflow-x: auto; -webkit-overflow-scrolling: touch; }
  `}</style>
);

// ============================================================
// RESPONSIVE HOOK
// ============================================================
function useWindowSize() {
  const [size, setSize] = React.useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 1024,
    height: typeof window !== 'undefined' ? window.innerHeight : 768,
  });
  React.useEffect(() => {
    const handle = () => setSize({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener('resize', handle);
    return () => window.removeEventListener('resize', handle);
  }, []);
  return size;
}

// Breakpoints
// mobile  : < 640
// tablet  : 640–1023
// desktop : >= 1024

// ============================================================
// SHARED COMPONENTS
// ============================================================
const Spinner = () => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '3rem', gap: 10 }}>
    <RefreshCw size={22} color={C.blue} style={{ animation: 'spin 1s linear infinite' }} />
    <span style={{ color: C.muted, fontSize: '0.85rem' }}>Loading…</span>
  </div>
);

const Toast = ({ msg, type }) => msg ? (
  <div style={{
    position: 'fixed', bottom: 'calc(16px + env(safe-area-inset-bottom,0px))', right: 16,
    left: 16, zIndex: 3000,
    background: type === 'err' ? '#450a0a' : '#052e16',
    border: `1px solid ${type === 'err' ? C.red : C.green}`,
    borderRadius: 12, padding: '12px 18px', color: '#fff',
    fontSize: '0.88rem', fontWeight: 600,
    display: 'flex', alignItems: 'center', gap: 8,
    animation: 'slideUp 0.3s ease',
    maxWidth: 480, margin: '0 auto',
    boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
  }}>
    {type === 'err' ? <AlertCircle size={16} color={C.red} /> : <CheckCircle size={16} color={C.green} />}
    {msg}
  </div>
) : null;

// Modal — full-screen on mobile, centered card on desktop
const Modal = ({ title, onClose, children, width = 440 }) => {
  const { width: w } = useWindowSize();
  const isMobile = w < 640;
  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: isMobile ? 'flex-end' : 'center', justifyContent: 'center', zIndex: 1000 }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{
        background: '#0D1B2F',
        border: `1px solid ${C.borderH}`,
        borderRadius: isMobile ? '20px 20px 0 0' : 18,
        padding: isMobile ? '20px 16px' : 26,
        width: isMobile ? '100%' : width,
        maxWidth: '100%',
        maxHeight: isMobile ? '92vh' : '88vh',
        overflowY: 'auto',
        animation: isMobile ? 'slideUp 0.25s ease' : 'fadeIn 0.2s ease',
        paddingBottom: isMobile ? 'calc(20px + env(safe-area-inset-bottom,0px))' : 26,
      }}>
        {/* drag handle on mobile */}
        {isMobile && <div style={{ width: 36, height: 4, background: C.dim, borderRadius: 2, margin: '0 auto 18px' }} />}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
          <h3 style={{ color: '#fff', fontSize: '1rem', fontWeight: 700, margin: 0 }}>{title}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: C.dim, cursor: 'pointer', display: 'flex', padding: 8, borderRadius: 8 }}><X size={18} /></button>
        </div>
        {children}
      </div>
    </div>
  );
};

const Confirm = ({ text, onConfirm, onCancel }) => {
  const { width: w } = useWindowSize();
  const isMobile = w < 640;
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: isMobile ? 'flex-end' : 'center', justifyContent: 'center', zIndex: 1100 }}>
      <div style={{ background: '#0D1B2F', border: '1px solid rgba(239,68,68,0.4)', borderRadius: isMobile ? '20px 20px 0 0' : 16, padding: isMobile ? '28px 20px' : 28, width: isMobile ? '100%' : 320, textAlign: 'center', animation: isMobile ? 'slideUp 0.25s ease' : 'fadeIn 0.2s ease', paddingBottom: isMobile ? 'calc(28px + env(safe-area-inset-bottom,0px))' : 28 }}>
        {isMobile && <div style={{ width: 36, height: 4, background: C.dim, borderRadius: 2, margin: '0 auto 18px' }} />}
        <div style={{ fontSize: '2rem', marginBottom: 10 }}>🗑</div>
        <h3 style={{ color: '#fff', fontWeight: 700, marginBottom: 8, fontSize: '1rem' }}>Confirm Delete</h3>
        <p style={{ color: C.muted, fontSize: '0.85rem', marginBottom: 20, lineHeight: 1.6 }}>{text}</p>
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={onCancel} style={{ flex: 1, padding: '12px', background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, color: C.muted, cursor: 'pointer', fontWeight: 600, fontSize: '0.9rem' }}>Cancel</button>
          <button onClick={onConfirm} style={{ flex: 1, padding: '12px', background: '#dc2626', border: 'none', borderRadius: 10, color: '#fff', cursor: 'pointer', fontWeight: 700, fontSize: '0.9rem' }}>Delete</button>
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, children }) => (
  <div style={{ marginBottom: 14 }}>
    <label style={{ color: C.muted, fontSize: '0.78rem', fontWeight: 600, display: 'block', marginBottom: 6 }}>{label}</label>
    {children}
  </div>
);

// fontSize 16px prevents iOS zoom on focus
const Inp = ({ value, onChange, placeholder, type = 'text', disabled = false }) => (
  <input
    value={value || ''} onChange={e => onChange(e.target.value)}
    type={type} placeholder={placeholder} disabled={disabled}
    style={{ width: '100%', background: 'rgba(15,23,42,0.8)', border: `1px solid ${C.border}`, borderRadius: 10, color: disabled ? C.dim : '#fff', padding: '11px 14px', fontSize: '16px', outline: 'none', transition: 'border-color 0.2s', WebkitAppearance: 'none' }}
    onFocus={e => e.target.style.borderColor = C.blue}
    onBlur={e => e.target.style.borderColor = C.border}
  />
);

const SaveBtn = ({ onClick, label = 'Save', loading = false }) => (
  <button onClick={onClick} disabled={loading}
    style={{ width: '100%', marginTop: 16, padding: '13px', background: loading ? C.dim : C.blue, border: 'none', borderRadius: 12, color: '#fff', fontSize: '0.95rem', fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, transition: 'all 0.2s', minHeight: 48 }}>
    {loading ? <RefreshCw size={15} style={{ animation: 'spin 1s linear infinite' }} /> : <Save size={15} />}
    {loading ? 'Saving…' : label}
  </button>
);

// Date picker — works on iOS/Android/Windows
const DateInput = ({ label, value, onChange, onClear, color }) => (
  <div style={{ flex: 1, minWidth: 0 }}>
    <label style={{ fontSize: '0.7rem', color, fontWeight: 700, marginBottom: 6, display: 'block', letterSpacing: '0.5px', textTransform: 'uppercase' }}>📅 {label}</label>
    <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
      <input type="date" value={value || ''} onChange={e => onChange(e.target.value)}
        style={{ width: '100%', background: 'rgba(15,23,42,0.9)', color: value ? '#fff' : '#64748b', border: `2px solid ${value ? color : color + '40'}`, borderRadius: 10, padding: '11px 40px 11px 12px', fontSize: '16px', fontWeight: 600, outline: 'none', cursor: 'pointer', transition: 'all 0.2s', colorScheme: 'dark', WebkitAppearance: 'none', minHeight: 44 }}
        onFocus={e => e.target.style.borderColor = color}
        onBlur={e => e.target.style.borderColor = value ? color : `${color}40`}
      />
      {value && (
        <button onClick={onClear}
          style={{ position: 'absolute', right: 8, background: 'rgba(239,68,68,0.2)', border: 'none', borderRadius: 6, color: '#ef4444', cursor: 'pointer', padding: '4px 7px', fontSize: '0.72rem', fontWeight: 700, lineHeight: 1, minHeight: 28, display: 'flex', alignItems: 'center' }}>✕</button>
      )}
    </div>
  </div>
);

// Dropdown — native-like on mobile, custom on desktop
const Dropdown = ({ label, value, options, onChange, placeholder, accentColor, openDD, setOpenDD }) => {
  const { width: w } = useWindowSize();
  const isMobile = w < 640;
  const id = `dd-${label.replace(/\s/g, '')}`;
  const isOpen = openDD === id;

  // On mobile use native <select> for better UX
  if (isMobile) return (
    <div style={{ flex: 1, minWidth: 0 }}>
      <label style={{ fontSize: '0.7rem', color: accentColor, fontWeight: 700, marginBottom: 6, display: 'block', letterSpacing: '0.5px', textTransform: 'uppercase' }}>{label}</label>
      <select value={value || ''} onChange={e => onChange(e.target.value)}
        style={{ width: '100%', background: '#0D1B2F', border: `2px solid ${accentColor}`, borderRadius: 10, color: '#fff', padding: '11px 12px', fontSize: '16px', fontWeight: 600, outline: 'none', WebkitAppearance: 'none', minHeight: 44 }}>
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );

  return (
    <div style={{ flex: 1, minWidth: 0, position: 'relative' }}>
      <label style={{ fontSize: '0.7rem', color: accentColor, fontWeight: 700, marginBottom: 6, display: 'block', letterSpacing: '0.5px', textTransform: 'uppercase' }}>{label}</label>
      <button onClick={() => setOpenDD(isOpen ? null : id)}
        style={{ width: '100%', background: C.card, border: `1px solid ${isOpen ? accentColor : C.border}`, borderRadius: 10, padding: '11px 12px', color: value ? '#fff' : C.dim, fontSize: '0.88rem', fontWeight: 600, cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', transition: 'all 0.2s', minHeight: 44 }}>
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '90%' }}>{value || placeholder}</span>
        <ChevronDown size={14} style={{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.2s', flexShrink: 0 }} />
      </button>
      {isOpen && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#0D1B2F', border: `1px solid ${accentColor}`, borderRadius: 10, marginTop: 4, maxHeight: 200, overflowY: 'auto', zIndex: 200, boxShadow: '0 8px 32px rgba(0,0,0,0.5)' }}>
          {options.map(o => (
            <div key={o} onClick={() => { onChange(o); setOpenDD(null); }}
              style={{ padding: '10px 12px', color: value === o ? '#fff' : C.muted, fontSize: '0.85rem', cursor: 'pointer', background: value === o ? `${accentColor}20` : 'transparent', transition: 'background 0.15s' }}
              onMouseEnter={e => e.currentTarget.style.background = `${accentColor}30`}
              onMouseLeave={e => e.currentTarget.style.background = value === o ? `${accentColor}20` : 'transparent'}>
              {value === o && '✓ '}{o}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ============================================================
// LOGIN FORM — shared for admin + student
// ============================================================
const LoginForm = ({ role, onLogin }) => {
  const { width: w } = useWindowSize();
  const isMobile = w < 640;
  const [email, setEmail] = useState('');
  const [pass, setPass] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errMsg, setErrMsg] = useState('');
  const isAdmin = role === 'admin';
  const accent = isAdmin ? C.blue : C.green;

  const submit = async (e) => {
    e?.preventDefault();
    if (!email || !pass) { setErrMsg('Email and password required'); return; }
    setLoading(true); setErrMsg('');

    try {
      const data = await api('/api/auth/login', 'POST', { email, password: pass, role });

      if (data?.Status === 'SUCCESS') {
        onLogin(data.user);
      } else {
        // ── Specific server-side error messages ───────────────
        const msg = (data?.Error || data?.Message || '').toLowerCase();
        if (msg.includes('not found') || msg.includes('user not found'))
          setErrMsg('No account found with this email address.');
        else if (msg.includes('wrong password') || msg.includes('incorrect password'))
          setErrMsg('Incorrect password — please try again.');
        else if (msg.includes('disabled'))
          setErrMsg('Your account has been disabled — contact your admin.');
        else if (msg.includes('not a student') || msg.includes('not an admin') || msg.includes('not a'))
          setErrMsg(`This account does not have ${role} access.`);
        else if (msg.includes('module') && msg.includes('access'))
          setErrMsg('You do not have access to this module — contact your admin.');
        else
          setErrMsg(data?.Error || data?.Message || 'Login failed — check your credentials.');
      }
    } catch (err) {
      // ── Specific network / connection error messages ──────────
      setErrMsg(getNetworkError(err));
    }

    setLoading(false);
  };

  return (
    <div style={{ minHeight: '100vh', minHeight: '100dvh', background: isAdmin ? `linear-gradient(135deg,${C.bg},#1a1f3a)` : `linear-gradient(135deg,${C.bg},#0d2a1f)`, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: isMobile ? '20px 16px' : 24 }}>
      <div style={{ width: '100%', maxWidth: 420, background: C.surface, borderRadius: 22, padding: isMobile ? '28px 20px' : 36, boxShadow: '0 20px 60px rgba(0,0,0,0.5)', border: `1px solid rgba(${isAdmin ? '74,144,226' : '16,185,129'},0.25)` }}>
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <div style={{
            width: 140, height: 140, borderRadius: 32, margin: '0 auto 18px',
            background: `linear-gradient(135deg, ${accent}, ${accent}80)`,
            padding: 3,
            boxShadow: `0 0 0 1px rgba(255,255,255,0.06), 0 16px 40px ${accent}55, 0 4px 12px rgba(0,0,0,0.4)`
          }}>
            <div style={{
              width: '100%', height: '100%', borderRadius: 29,
              background: 'radial-gradient(circle at 30% 20%, #1a1a1a, #000)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 4
            }}>
              <img src={KITBIT_LOGO} alt="KITBIT" style={{ width: '100%', height: '100%', objectFit: 'contain', filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.5))' }} />
            </div>
          </div>
          <div style={{ fontSize: '0.7rem', color: C.dim, letterSpacing: 3 }}>{isAdmin ? 'MANAGEMENT CONSOLE' : 'VR TRAINING PORTAL'}</div>
          <div style={{ marginTop: 10, display: 'inline-block', padding: '4px 14px', background: `${accent}20`, border: `1px solid ${accent}40`, borderRadius: 20, fontSize: '0.72rem', color: accent, fontWeight: 700 }}>
            {isAdmin ? '🔒 Admin Access' : '🎓 Student Access'}
          </div>
        </div>
        <form onSubmit={submit}>
          <Field label="Email">
            <Inp value={email} onChange={setEmail} placeholder={isAdmin ? 'admin@kitbit.com' : 'john@himt.com'} type="email" />
          </Field>
          <Field label="Password">
            <div style={{ position: 'relative' }}>
              <Inp value={pass} onChange={setPass} placeholder="••••••••" type={showPass ? 'text' : 'password'} />
              <button type="button" onClick={() => setShowPass(!showPass)}
                style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: C.dim, cursor: 'pointer', display: 'flex', padding: 6, minHeight: 44, alignItems: 'center' }}>
                {showPass ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </Field>
          {errMsg && (
            <div style={{ color: C.red, fontSize: '0.82rem', marginBottom: 14, padding: '10px 14px', background: 'rgba(239,68,68,0.1)', borderRadius: 10, display: 'flex', alignItems: 'center', gap: 7 }}>
              <AlertCircle size={14} />{errMsg}
            </div>
          )}
          <button type="submit" disabled={loading}
            style={{ width: '100%', marginTop: 8, padding: '14px', background: loading ? C.dim : accent, border: 'none', borderRadius: 13, color: '#fff', fontSize: '1rem', fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7, transition: 'all 0.2s', boxShadow: loading ? 'none' : `0 4px 18px ${accent}50`, minHeight: 52 }}>
            {loading ? <><RefreshCw size={16} style={{ animation: 'spin 1s linear infinite' }} /> Signing in…</> : 'Sign In'}
          </button>
        </form>
        <div style={{ marginTop: 24, paddingTop: 16, borderTop: `1px solid ${C.border}`, textAlign: 'center' }}>
          <div style={{ fontSize: '0.7rem', color: C.dim }}>
            © 2026 All rights reserved - <a href="https://kitbit.tech/" target="_blank" rel="noopener noreferrer" style={{ color: accent, textDecoration: 'none', fontWeight: 700 }}>KITBIT</a>
          </div>
        </div>
      </div>
    </div>
  );
};

const StudentLoginPage = ({ onLogin }) => <LoginForm role="student" onLogin={onLogin} />;
const AdminLoginPage = ({ onLogin }) => <LoginForm role="admin" onLogin={onLogin} />;

// ============================================================
// MODULES VIEW
// ============================================================
const ModulesView = ({ modules, loading, isAdmin, onSelect, onRefresh }) => {
  const { width: w } = useWindowSize();
  const isMobile = w < 640;
  if (loading) return <Spinner />;
  if (!modules.length) return (
    <div style={{ textAlign: 'center', padding: '4rem 1rem', color: C.dim }}>
      {isAdmin ? 'No modules yet — add one in Admin Panel' : 'No modules assigned to your account yet.'}
    </div>
  );
  return (
    <div>
      {isAdmin && (
        <div style={{ marginBottom: 14, padding: '8px 14px', background: 'rgba(74,144,226,0.08)', border: `1px solid ${C.border}`, borderRadius: 10, fontSize: '0.8rem', color: C.muted, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Shield size={13} color={C.blue} /> Admin — all {modules.length} active modules</span>
          <button onClick={onRefresh} style={{ background: 'none', border: 'none', color: C.blue, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.78rem', padding: '4px 8px' }}>
            <RefreshCw size={12} /> Refresh
          </button>
        </div>
      )}
      {/* Responsive grid: 1 col mobile, 2 col tablet, 3+ col desktop */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill,minmax(280px,1fr))', gap: isMobile ? 12 : 18 }}>
        {modules.map(m => (
          <div key={m.id} onClick={() => onSelect(m)}
            style={{ background: C.card, borderRadius: 18, overflow: 'hidden', cursor: 'pointer', border: `1px solid ${C.border}`, transition: 'all 0.3s', WebkitTapHighlightColor: 'transparent' }}
            onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.borderColor = m.color; e.currentTarget.style.boxShadow = `0 12px 40px ${m.color}30`; }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.borderColor = C.border; e.currentTarget.style.boxShadow = 'none'; }}>
            <div style={{ height: isMobile ? 80 : 110, position: 'relative', overflow: 'hidden' }}>
              {m.image ? (
                <img
                  src={m.image}
                  alt={m.name}
                  style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center' }}
                />
              ) : (
                <div style={{ height: '100%', background: `linear-gradient(135deg,${m.color}70,${m.color}25)`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: isMobile ? '2.4rem' : '3.2rem' }}>
                  {m.icon}
                </div>
              )}
            </div>
            <div style={{ padding: isMobile ? 14 : 18 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10, gap: 8 }}>
                <div>
                  <h3 style={{ color: '#fff', fontSize: isMobile ? '0.95rem' : '1rem', fontWeight: 700, margin: '0 0 4px' }}>{m.name}</h3>
                  <div style={{ fontSize: '0.73rem', color: C.muted }}>{m.completedTasks} of {m.totalTasks} tasks done</div>
                </div>
                <div style={{ background: `${m.color}40`, color: '#fff', padding: '4px 10px', borderRadius: 9, fontSize: '0.85rem', fontWeight: 800, flexShrink: 0 }}>{m.progress}%</div>
              </div>
              <div style={{ background: 'rgba(15,23,42,0.8)', borderRadius: 999, height: 6, overflow: 'hidden', marginBottom: 12 }}>
                <div style={{ background: m.color, height: '100%', width: `${m.progress}%`, borderRadius: 999, transition: 'width 0.6s ease', boxShadow: `0 0 8px ${m.color}80` }} />
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {m.hasGuided && <span style={{ padding: '3px 9px', background: `${m.color}20`, color: m.color, borderRadius: 6, fontSize: '0.7rem', fontWeight: 700, border: `1px solid ${m.color}40` }}>Training</span>}
                {m.hasAssessment && <span style={{ padding: '3px 9px', background: `${m.color}20`, color: m.color, borderRadius: 6, fontSize: '0.7rem', fontWeight: 700, border: `1px solid ${m.color}40` }}>Assessment</span>}
                <span style={{ padding: '3px 9px', background: 'rgba(100,116,139,0.15)', color: C.dim, borderRadius: 6, fontSize: '0.7rem' }}>{m.tasks?.length || 0} tasks</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ============================================================
// ANALYTICS VIEW
// ============================================================
// ============================================================
// TRAINING REPORT PANEL (Welding VR, module 18)
// Shown only when the selected session has a "report" (data sent by
// Unity with the steps). Other modules never send one -> unaffected.
// ============================================================
const ReportBar = ({ label, value }) => {
  const v = Math.max(0, Math.min(100, Number(value) || 0));
  const col = v >= 70 ? C.green : v >= 40 ? C.amber : C.red;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 7 }}>
      <span style={{ width: 120, flexShrink: 0, fontSize: '0.74rem', color: C.muted }}>{label}</span>
      <div style={{ flex: 1, height: 8, background: 'rgba(148,163,184,0.15)', borderRadius: 4, overflow: 'hidden' }}>
        <div style={{ width: `${v}%`, height: '100%', background: col, borderRadius: 4 }} />
      </div>
      <span style={{ width: 34, textAlign: 'right', fontSize: '0.74rem', fontWeight: 700, color: col }}>{v}</span>
    </div>
  );
};

const WeldReportPanel = ({ r, color, isMobile }) => {
  if (!r || !r.grade) return null;
  const box = { background: 'rgba(15,23,42,0.6)', border: `1px solid ${C.border}`, borderRadius: 10, padding: '12px 14px', flex: 1, minWidth: isMobile ? '100%' : 260 };
  const head = { fontSize: '0.72rem', fontWeight: 800, letterSpacing: 1, color, marginBottom: 10 };
  const passCol = r.passed ? C.green : C.red;
  const sub = [r.process, r.position, r.joint, r.material].filter(Boolean).join('  |  ');
  return (
    <div style={{ marginTop: 14, background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, overflow: 'hidden' }}>
      <div style={{ background: 'rgba(15,23,42,0.9)', padding: '10px 14px', borderBottom: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
        <div>
          <div style={{ color: '#fff', fontSize: '0.88rem', fontWeight: 700 }}>Training Report</div>
          {sub && <div style={{ color: C.dim, fontSize: '0.68rem' }}>{sub}</div>}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontSize: '0.8rem', fontWeight: 800, color: '#fff' }}>Score {r.total ?? 0}/100</span>
          <span style={{ fontSize: '0.75rem', fontWeight: 800, padding: '3px 10px', borderRadius: 6, background: 'rgba(74,144,226,0.15)', color: C.blue }}>Grade {r.grade}</span>
          <span style={{ fontSize: '0.75rem', fontWeight: 800, padding: '3px 10px', borderRadius: 6, background: `${passCol}22`, color: passCol }}>{r.passed ? 'PASSED' : 'NOT PASSED'}</span>
        </div>
      </div>
      <div style={{ padding: 14, display: 'flex', gap: 14, flexWrap: 'wrap' }}>
        <div style={box}>
          <div style={head}>INPUT SKILLS</div>
          <ReportBar label="Arc gap" value={r.arcGap} />
          <ReportBar label="Travel speed" value={r.travelSpeed} />
          <ReportBar label="Work angle" value={r.workAngle} />
          <ReportBar label="Travel angle" value={r.travelAngle} />
          <ReportBar label="Machine setup" value={r.machineSetup} />
          <ReportBar label="Seam coverage" value={r.seamCoverage} />
          {!!r.defectPenalty && <div style={{ fontSize: '0.72rem', color: C.red, marginTop: 4 }}>Defect penalty: -{r.defectPenalty}</div>}
        </div>
        <div style={box}>
          <div style={head}>QUALITY SCORES</div>
          <ReportBar label="Penetration" value={r.penetration} />
          <ReportBar label="Porosity" value={r.porosity} />
          <ReportBar label="Spatter" value={r.spatter} />
          <ReportBar label="Bead uniformity" value={r.beadUniformity} />
          <ReportBar label="Inclusions" value={r.inclusions} />
          <ReportBar label="Overall quality" value={r.qualityOverall} />
        </div>
        <div style={box}>
          <div style={head}>DEFECTS FOUND ({r.defectCount ?? 0})</div>
          {(r.defects || []).length === 0
            ? <div style={{ fontSize: '0.74rem', color: C.green }}>No defects</div>
            : (r.defects || []).map((d, i) => (
              <div key={i} style={{ marginBottom: 7 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.74rem' }}>
                  <span style={{ color: '#fff', fontWeight: 600 }}>{toTitleCase((d.type || '').replace(/([a-z])([A-Z])/g, '$1 $2'))}</span>
                  <span style={{ color: C.amber, fontWeight: 700 }}>x{d.count}</span>
                </div>
                {d.cause && <div style={{ fontSize: '0.66rem', color: C.dim }}>{d.cause}</div>}
              </div>
            ))}
          {(r.tips || []).length > 0 && (
            <>
              <div style={{ ...head, marginTop: 12 }}>TIPS</div>
              {r.tips.map((t, i) => <div key={i} style={{ fontSize: '0.72rem', color: C.muted, marginBottom: 4 }}>• {t}</div>)}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const AnalyticsView = ({ module, user, isAdmin, allStudents }) => {
  const { width: w } = useWindowSize();
  const isMobile = w < 640;
  const isTablet = w >= 640 && w < 1024;

  // ── Per-module UI tweaks from client config ───────────────
  const modUI = getModuleUI(module.id);

  const [sessions, setSessions] = useState([]);
  const [selUser, setSelUser] = useState(user.id.toString());
  const [selSession, setSelSession] = useState(null);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [datesSet, setDatesSet] = useState(false);
  const [selMode, setSelMode] = useState('guided');
  const [openDD, setOpenDD] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sessionTimes, setSessionTimes] = useState({ startTime: '', endTime: '' });
  const [showSessions, setShowSessions] = useState(false);
  const [selTask, setSelTask] = useState('all');

  const fmt = d => d ? new Date(d).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '—';
  const fmtDate = d => d ? new Date(d).toLocaleDateString('en-GB') : '—';
  const formatTime = s => {
    if (!s || s === 0) return '0m';
    if (s < 60) return `${s}s`;
    const m = Math.floor(s / 60), rem = s % 60;
    if (m < 60) return rem > 0 ? `${m}m ${rem}s` : `${m}m`;
    return `${Math.floor(m / 60)}h ${m % 60}m`;
  };

  const buildSteps = (sess) => (sess.steps || []).map(st => ({
    title: toTitleCase(st.step_name),
    status: st.performed ? 'Completed' : 'Not Done',
    startT: st.started_at ? fmt(new Date(st.started_at)) : '—',
    endT: st.ended_at ? fmt(new Date(st.ended_at)) : '—',
    secs: st.time_taken_sec || 0,
    scoreAwarded: st.score_awarded ?? null,
    assessmentCorrect: st.assessment_correct ?? null,
    taskName: st.task_name || null,
    taskOrder: st.task_order ?? null,
  }));

  const getSessionNumber = (sess) => {
    if (!sess || !sessions.length) return null;
    const idx = sessions.findIndex(s => s.id === sess.id);
    return idx >= 0 ? sessions.length - idx : null;
  };

  const toDateStr = d => {
    if (!d) return null;
    const dt = new Date(d.replace(' ', 'T'));
    return isNaN(dt) ? null : dt.toISOString().slice(0, 10);
  };

  const filteredSessions = sessions.filter(s => {
    if (module.hasGuided && module.hasAssessment && (s.mode || 'guided').toLowerCase() !== selMode) return false;
    if (!datesSet || (!from && !to)) return true;
    const d = toDateStr(s.started_at || s.created_at);
    if (!d) return true;
    return (!from || d >= from) && (!to || d <= to);
  });

  const displaySteps = selSession ? buildSteps(selSession) : filteredSessions.flatMap(s => buildSteps(s));
  // Multi-task selector — only for modules flagged multiTask in MODULE_UI_CONFIG
  const taskKeys = modUI.multiTask ? (module.tasks || []) : [];  // task list comes from the module definition, so the dropdown shows even with no sessions yet
  const showTaskDropdown = modUI.multiTask && taskKeys.length > 1;
  const visibleSteps = (showTaskDropdown && selTask !== 'all') ? displaySteps.filter(s => sameTask(modUI, s.taskName, selTask)) : displaySteps;
  const totalTime = selSession ? (selSession.total_time_sec || 0) : filteredSessions.reduce((a, s) => a + (s.total_time_sec || 0), 0);
  const isAssessmentMode = selSession ? (selSession.mode || '').toLowerCase() === 'assessment' : selMode === 'assessment' || (!module.hasGuided && module.hasAssessment);

  // ============================================================
  // NEW: overall session score out of 500 (5 tasks x 100 each -
  // Tour, MCQ, Dump Truck, Truck, Car). Sums scoreAwarded across
  // ALL steps in the current session, regardless of which task
  // they belong to, since each task's own scoring already caps at
  // 100 (see StepManager's per-step/per-task scoring on the Unity
  // side). If no session is selected, sums across all filtered
  // sessions' own totals instead.
  // ============================================================
  // CHANGED: only shows/computes for modules that opt in via
  // totalScoreMax in MODULE_UI_CONFIG (currently just Ro Ro Ship,
  // module_id 3). Every other module is completely unaffected.
  const MAX_TOTAL_SCORE = modUI.totalScoreMax || null;
  // CHANGED: only show the SCORE card in Assessment mode - Guided mode
  // doesn't track scoring the same way (no correct/wrong marking), so
  // the 500-point total isn't meaningful there.
  const showTotalScore = !!MAX_TOTAL_SCORE && isAssessmentMode;
  const totalScore = showTotalScore
    ? (selSession
      ? displaySteps.reduce((sum, s) => sum + (s.scoreAwarded || 0), 0)
      : filteredSessions.reduce((sum, s) => sum + (s.score || 0), 0))
    : 0;

  const loadSessions = useCallback(async (uid) => {
    if (!uid) return;
    setLoading(true);
    const data = await api(`/api/vr/sessions/${uid}/${module.id}`);
    if (data?.Status === 'SUCCESS') {
      const s = data.sessions || [];
      setSessions(s);
      setSelSession(s.length > 0 ? s[0] : null);
      if (s.length > 0) setSelMode((s[0].mode || 'guided').toLowerCase());
      if (s.length > 0) {
        const times = s.filter(x => x.started_at);
        setSessionTimes({
          startTime: times.length > 0 ? new Date(Math.min(...times.map(x => new Date(x.started_at)))).toLocaleString('en-GB') : '-',
          endTime: times.length > 0 ? new Date(Math.max(...times.map(x => new Date(x.ended_at || x.created_at)))).toLocaleString('en-GB') : '-',
        });
      } else { setSessionTimes({ startTime: '-', endTime: '-' }); }
    } else { setSessions([]); setSelSession(null); setSessionTimes({ startTime: '-', endTime: '-' }); }
    setLoading(false);
  }, [module.id]);

  useEffect(() => {
    const uid = selUser || user.id.toString();
    if (uid) { setFrom(''); setTo(''); setDatesSet(false); setSelMode('guided'); setSelTask('all'); loadSessions(uid); }
  }, [selUser, loadSessions, user.id]);

  const handleFromChange = v => {
    setFrom(v); setDatesSet(true);
    const f = sessions.filter(s => { const d = toDateStr(s.started_at || s.created_at); return (!v || !d || d >= v) && (!to || !d || d <= to); });
    setSelSession(f.length > 0 ? f[0] : null);
  };
  const handleToChange = v => {
    setTo(v); setDatesSet(true);
    const f = sessions.filter(s => { const d = toDateStr(s.started_at || s.created_at); return (!from || !d || d >= from) && (!v || !d || d <= v); });
    setSelSession(f.length > 0 ? f[0] : null);
  };

  return (
    <div>
      {/* Module Header */}
      <div style={{ background: `linear-gradient(135deg,${module.color}40,${module.color}20)`, borderRadius: 16, padding: isMobile ? '12px 14px' : '18px 24px', marginBottom: 10, border: `1px solid ${module.color}60`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 10 : 16, minWidth: 0 }}>
          <div style={{ width: isMobile ? 90 : 120, height: isMobile ? 58 : 76, fontSize: isMobile ? '2rem' : '2.8rem', background: `${module.color}30`, borderRadius: 12, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
            {module.image ? <img src={module.image} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} /> : module.icon}
          </div>
          <div style={{ minWidth: 0 }}>
            <h2 style={{ color: '#fff', fontSize: isMobile ? '0.95rem' : '1.4rem', fontWeight: 800, margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: isMobile ? 'nowrap' : 'normal' }}>{module.name}</h2>
            {!isMobile && (
              <div style={{ display: 'flex', gap: 16, color: '#cbd5e1', fontSize: '0.76rem', marginTop: 6, flexWrap: 'wrap' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><Calendar size={12} color={module.color} /><span style={{ color: C.muted }}>Start:</span><strong style={{ color: '#fff' }}>{sessionTimes.startTime || '—'}</strong></span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><Calendar size={12} color={module.color} /><span style={{ color: C.muted }}>End:</span><strong style={{ color: '#fff' }}>{sessionTimes.endTime || '—'}</strong></span>
              </div>
            )}
          </div>
        </div>
        <button onClick={async () => {
          const savedFrom = from;
          const savedTo = to;
          const savedDatesSet = datesSet;
          const savedMode = selMode;
          const uid = selUser || user.id;
          if (!uid) return;
          setLoading(true);
          const data = await api(`/api/vr/sessions/${uid}/${module.id}`);
          if (data?.Status === 'SUCCESS') {
            const s = data.sessions || [];
            setSessions(s);
            setFrom(savedFrom);
            setTo(savedTo);
            setDatesSet(savedDatesSet);
            setSelMode(savedMode);
            const filtered = s.filter(x => {
              if ((x.mode || 'guided').toLowerCase() !== savedMode) return false;
              if (!savedDatesSet || (!savedFrom && !savedTo)) return true;
              const d = toDateStr(x.started_at || x.created_at);
              if (!d) return true;
              return (!savedFrom || d >= savedFrom) && (!savedTo || d <= savedTo);
            });
            setSelSession(filtered.length > 0 ? filtered[0] : null);
            const times = s.filter(x => x.started_at);
            setSessionTimes({
              startTime: times.length > 0 ? new Date(Math.min(...times.map(x => new Date(x.started_at)))).toLocaleString('en-GB') : '-',
              endTime: times.length > 0 ? new Date(Math.max(...times.map(x => new Date(x.ended_at || x.created_at)))).toLocaleString('en-GB') : '-',
            });
          } else { setSessions([]); setSelSession(null); }
          setLoading(false);
        }}
          style={{ background: 'rgba(255,255,255,0.12)', border: 'none', borderRadius: 9, color: '#fff', padding: isMobile ? '8px' : '8px 14px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5, fontSize: '0.8rem', fontWeight: 600, flexShrink: 0, minHeight: 40 }}>
          <RefreshCw size={13} />{!isMobile && ' Refresh'}
        </button>
      </div>

      {/* Filters */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : isTablet ? 'repeat(2,1fr)' : 'repeat(4,1fr)', gap: 8, marginBottom: 10 }}>
        {isAdmin
          ? <div style={{ gridColumn: isMobile ? '1 / -1' : 'auto' }}>
            <Dropdown label="Student" value={allStudents.find(u => u.id.toString() === selUser)
              ? `${allStudents.find(u => u.id.toString() === selUser).name} (${allStudents.find(u => u.id.toString() === selUser).email})`
              : `${user.name} (${user.email})`}
              options={allStudents.map(u => `${u.name} (${u.email})`)}
              onChange={val => { const u = allStudents.find(x => `${x.name} (${x.email})` === val); setSelUser(u ? u.id.toString() : user.id.toString()); }}
              placeholder={user.email} accentColor={module.color} openDD={openDD} setOpenDD={setOpenDD} />
          </div>
          : <div style={{ gridColumn: isMobile ? '1 / -1' : 'auto', background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: '11px 12px', fontSize: '0.88rem', color: C.muted, display: 'flex', alignItems: 'center', gap: 7, minHeight: 44 }}>
            <User size={13} color={C.blue} /><span style={{ color: '#fff', fontWeight: 600 }}>{user.name}</span>
          </div>
        }
        <DateInput label="FROM" value={from} onChange={handleFromChange}
          onClear={() => { setFrom(''); setTo(''); setDatesSet(false); setSelSession(sessions.length > 0 ? sessions[0] : null); }} color={module.color} />
        <DateInput label="TO" value={to} onChange={handleToChange}
          onClear={() => { setFrom(''); setTo(''); setDatesSet(false); setSelSession(sessions.length > 0 ? sessions[0] : null); }} color={module.color} />
        {module.hasGuided && module.hasAssessment && (
          <div style={{ gridColumn: isMobile ? '1 / -1' : 'auto' }}>
            <label style={{ fontSize: '0.7rem', color: module.color, fontWeight: 700, marginBottom: 6, display: 'block', letterSpacing: '0.5px', textTransform: 'uppercase' }}>🎯 MODE</label>
            <div style={{ display: 'flex', gap: 6 }}>
              {['guided', 'assessment'].map(m => {
                const handleModeClick = () => {
                  setSelMode(m);
                  const modeFiltered = sessions.filter(s => {
                    const sMode = (s.mode || 'guided').toLowerCase();
                    if (sMode !== m) return false;
                    if (!datesSet || (!from && !to)) return true;
                    const d = toDateStr(s.started_at || s.created_at);
                    if (!d) return true;
                    return (!from || d >= from) && (!to || d <= to);
                  });
                  setSelSession(modeFiltered.length > 0 ? modeFiltered[0] : null);
                };
                return (
                  <button key={m} onClick={handleModeClick}
                    style={{ flex: 1, padding: '11px 6px', borderRadius: 9, fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer', border: 'none', background: selMode === m ? module.color : 'rgba(30,41,59,0.6)', color: '#fff', transition: 'all 0.2s', minHeight: 44 }}>
                    {m === 'guided' ? 'Guided' : 'Assessment'}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2,1fr)' : `repeat(${showTotalScore ? 5 : 4},1fr)`, gap: isMobile ? 6 : 8, marginBottom: 10 }}>
        <div style={{ background: C.card, border: `1px solid ${selSession ? module.color + '60' : C.border}`, borderRadius: 11, padding: isMobile ? '10px 10px' : '12px 14px' }}>
          <div style={{ fontSize: isMobile ? '0.58rem' : '0.62rem', color: selSession ? module.color : C.muted, fontWeight: 700, marginBottom: 4 }}>📊 {selSession ? 'SESSION' : 'SESSIONS'}</div>
          <div style={{ fontSize: isMobile ? '1.2rem' : '1.4rem', fontWeight: 800, color: '#fff' }}>{selSession ? getSessionNumber(selSession) : filteredSessions.length}</div>
        </div>
        {[
          { icon: '⏱', label: 'TIME', value: formatTime(totalTime) },
          { icon: '✅', label: 'DONE', value: visibleSteps.filter(s => s.status === 'Completed').length },
          { icon: '📋', label: 'STEPS', value: visibleSteps.length },
        ].map(({ icon, label, value }) => (
          <div key={label} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 11, padding: isMobile ? '10px 10px' : '12px 14px' }}>
            <div style={{ fontSize: isMobile ? '0.58rem' : '0.62rem', color: C.muted, fontWeight: 700, marginBottom: 4 }}>{icon} {label}</div>
            <div style={{ fontSize: isMobile ? '1.2rem' : '1.4rem', fontWeight: 800, color: '#fff' }}>{value}</div>
          </div>
        ))}
        {/* CHANGED: only rendered for modules with totalScoreMax set
            (currently just Ro Ro Ship / module_id 3). Every other
            module's stats grid is completely unaffected. */}
        {showTotalScore && (
          <div style={{ background: C.card, border: `1px solid ${totalScore >= MAX_TOTAL_SCORE ? C.green + '60' : C.border}`, borderRadius: 11, padding: isMobile ? '10px 10px' : '12px 14px' }}>
            <div style={{ fontSize: isMobile ? '0.58rem' : '0.62rem', color: C.muted, fontWeight: 700, marginBottom: 4 }}>🏆 SCORE</div>
            <div style={{ fontSize: isMobile ? '1.2rem' : '1.4rem', fontWeight: 800, color: totalScore >= MAX_TOTAL_SCORE ? C.green : '#fff' }}>
              {totalScore}
            </div>
          </div>
        )}
      </div>

      {/* Sessions toggle button on mobile */}
      {isMobile && (
        <button onClick={() => setShowSessions(!showSessions)}
          style={{ width: '100%', padding: '11px', background: showSessions ? module.color : 'rgba(30,41,59,0.8)', border: `1px solid ${module.color}60`, borderRadius: 10, color: '#fff', fontSize: '0.85rem', fontWeight: 700, cursor: 'pointer', marginBottom: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, minHeight: 44 }}>
          {showSessions ? '▲ Hide Sessions' : `▼ Sessions (${filteredSessions.length})`}
        </button>
      )}

      {/* Main content */}
      <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 10 }}>

        {/* Sessions Panel — hidden on mobile unless toggled */}
        {(!isMobile || showSessions) && (
          <div style={{ width: isMobile ? '100%' : isTablet ? 200 : 220, flexShrink: 0, background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, display: 'flex', flexDirection: 'column', overflow: 'hidden', maxHeight: isMobile ? 260 : 'calc(100vh - 420px)', minHeight: isMobile ? 0 : 200 }}>
            <div style={{ padding: '10px 13px', borderBottom: `1px solid ${C.border}`, background: 'rgba(15,23,42,0.9)' }}>
              <div style={{ fontSize: '0.68rem', color: module.color, fontWeight: 700, letterSpacing: '0.8px' }}>SESSIONS ({filteredSessions.length})</div>
              {selSession && <div style={{ marginTop: 2, fontSize: '0.7rem', fontWeight: 800, color: module.color }}>▶ Session {getSessionNumber(selSession)}</div>}
            </div>
            <div style={{ overflowY: 'auto', flex: 1 }}>
              {loading ? <Spinner /> : filteredSessions.length === 0
                ? <div style={{ padding: '2rem', textAlign: 'center', color: C.dim, fontSize: '0.78rem' }}>No sessions found</div>
                : filteredSessions.map((sess) => {
                  const isSel = selSession?.id === sess.id;
                  const steps = sess.steps || [];
                  const done = steps.filter(s => s.performed).length;
                  const sNum = getSessionNumber(sess);
                  const d = sess.started_at ? new Date(sess.started_at) : null;
                  const isAssmt = (sess.mode || '').toLowerCase() === 'assessment';
                  return (
                    <div key={sess.id} onClick={() => { setSelSession(sess); setSelTask('all'); if (isMobile) setShowSessions(false); }}
                      style={{ padding: '10px 13px', borderBottom: `1px solid rgba(74,144,226,0.07)`, cursor: 'pointer', background: isSel ? `${module.color}22` : 'transparent', borderLeft: isSel ? `3px solid ${module.color}` : '3px solid transparent', transition: 'all 0.15s', minHeight: 54 }}
                      onMouseEnter={e => { if (!isSel) e.currentTarget.style.background = 'rgba(74,144,226,0.06)'; }}
                      onMouseLeave={e => { if (!isSel) e.currentTarget.style.background = 'transparent'; }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                        <span style={{ fontSize: '0.82rem', fontWeight: 700, color: isSel ? module.color : '#fff' }}>Session {sNum}</span>
                        {/* CHANGED: for Ro Ro Ship (modules with totalScoreMax
                            set), the old "Score: X/100" doesn't apply anymore
                            since scoring is now combined across 5 tasks out
                            of 500 - shown in the top SCORE card instead. Just
                            show done/steps count here regardless of mode.
                            Every other module keeps the original behavior. */}
                        {isAssmt && !modUI.totalScoreMax
                          ? <span style={{ fontSize: '0.68rem', fontWeight: 700, color: C.green }}>Score: {sess.score ?? 0}/100</span>
                          : <span style={{ fontSize: '0.68rem', fontWeight: 700, color: done === steps.length && steps.length > 0 ? C.green : C.amber }}>{done}/{steps.length}</span>
                        }
                      </div>
                      <div style={{ fontSize: '0.68rem', color: C.muted }}>{d ? fmtDate(d) : '—'} • {formatTime(sess.total_time_sec)}</div>
                    </div>
                  );
                })
              }
            </div>
          </div>
        )}

        {/* Steps Table */}
        <div style={{ flex: 1, background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: isMobile ? 300 : 0, maxHeight: isMobile ? 'none' : 'calc(100vh - 420px)' }}>
          <div style={{ background: 'rgba(15,23,42,0.9)', padding: '10px 14px', borderBottom: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap', flexShrink: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', minWidth: 0 }}>
              {selSession ? (
                <>
                  <span style={{ background: module.color, color: '#fff', fontWeight: 800, fontSize: '0.75rem', padding: '3px 12px', borderRadius: 20, whiteSpace: 'nowrap' }}>Session {getSessionNumber(selSession)}</span>
                  {!isMobile && <span style={{ color: '#fff', fontSize: '0.85rem', fontWeight: 700 }}>Step Details</span>}
                </>
              ) : (
                <span style={{ color: '#fff', fontSize: '0.88rem', fontWeight: 700 }}>All Steps</span>
              )}
            </div>
            {showTaskDropdown && (
              <select value={selTask} onChange={e => setSelTask(e.target.value)}
                style={{ background: 'rgba(15,23,42,0.95)', color: '#fff', border: `1px solid ${module.color}80`, borderRadius: 6, padding: '4px 8px', fontSize: '0.72rem', fontWeight: 700, maxWidth: 220, cursor: 'pointer' }}>
                <option value="all">All tasks ({displaySteps.length})</option>
                {taskKeys.map(t => <option key={t} value={t}>{toTitleCase(t)} ({displaySteps.filter(s => sameTask(modUI, s.taskName, t)).length})</option>)}
              </select>
            )}
            <span style={{ fontSize: '0.68rem', color: C.muted, background: 'rgba(74,144,226,0.1)', padding: '3px 9px', borderRadius: 6, flexShrink: 0 }}>{visibleSteps.length} rows</span>
          </div>
          {/* Horizontal scroll on mobile */}
          <div className="table-wrap" style={{ overflowY: 'auto', overflowX: 'auto', flex: 1 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: isMobile ? 480 : 500 }}>
              <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                <tr style={{ background: 'rgba(15,23,42,0.97)' }}>
                  {[
                    'Step Name', 'Start', 'End', 'Time', 'Done',
                    ...(isAssessmentMode && module.hasAssessment ? [modUI.scoreLabel || 'Score'] : [])
                  ].map(h => (
                    <th key={h} style={{ padding: isMobile ? '8px 10px' : '10px 13px', textAlign: h === 'Step Name' ? 'left' : 'right', color: module.color, fontWeight: 700, fontSize: '0.63rem', textTransform: 'uppercase', letterSpacing: '0.8px', borderBottom: `1px solid ${C.border}`, whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visibleSteps.length === 0
                  ? <tr><td colSpan={isAssessmentMode && module.hasAssessment ? 6 : 5} style={{ padding: '3rem', textAlign: 'center', color: C.dim, fontSize: '0.85rem' }}>No steps recorded yet</td></tr>
                  : visibleSteps.map((item, i) => (
                    <tr key={i} style={{ borderBottom: `1px solid rgba(74,144,226,0.07)`, transition: 'background 0.15s' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'rgba(74,144,226,0.06)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <td style={{ padding: isMobile ? '9px 10px' : '10px 13px', color: '#fff', fontSize: '0.82rem', fontWeight: 500, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.title}</td>
                      <td style={{ padding: isMobile ? '9px 10px' : '10px 13px', textAlign: 'right', color: C.muted, fontSize: '0.74rem', whiteSpace: 'nowrap' }}>{item.startT}</td>
                      <td style={{ padding: isMobile ? '9px 10px' : '10px 13px', textAlign: 'right', color: C.muted, fontSize: '0.74rem', whiteSpace: 'nowrap' }}>{item.endT}</td>
                      <td style={{ padding: isMobile ? '9px 10px' : '10px 13px', textAlign: 'right', color: C.dim, fontSize: '0.72rem', whiteSpace: 'nowrap' }}>{item.secs > 0 ? `${item.secs}s` : '—'}</td>
                      <td style={{ padding: isMobile ? '9px 10px' : '10px 13px', textAlign: 'right' }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, padding: '3px 8px', borderRadius: 6, fontSize: '0.68rem', fontWeight: 700, background: item.status === 'Completed' ? 'rgba(16,185,129,0.15)' : 'rgba(100,116,139,0.15)', color: item.status === 'Completed' ? C.green : C.dim }}>
                          {item.status === 'Completed' ? <CheckCircle size={10} /> : <XCircle size={10} />}
                          {item.status === 'Completed' ? 'Yes' : 'No'}
                        </span>
                      </td>
                      {/* Score column — driven by API flag, not module ID */}
                      {isAssessmentMode && module.hasAssessment && (
                        <td style={{ padding: isMobile ? '9px 10px' : '10px 13px', textAlign: 'right' }}>
                          {item.scoreAwarded !== null
                            ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, padding: '3px 8px', borderRadius: 6, fontSize: '0.68rem', fontWeight: 700, background: (item.assessmentCorrect || (modUI.partialScore && item.scoreAwarded > 0)) ? 'rgba(16,185,129,0.15)' : 'rgba(239,68,68,0.12)', color: (item.assessmentCorrect || (modUI.partialScore && item.scoreAwarded > 0)) ? C.green : '#ef4444' }}>
                              {(item.assessmentCorrect || (modUI.partialScore && item.scoreAwarded > 0)) ? `+${item.scoreAwarded}` : '0'}
                            </span>
                            : <span style={{ color: C.dim, fontSize: '0.72rem' }}>—</span>
                          }
                        </td>
                      )}
                    </tr>
                  ))
                }
              </tbody>
            </table>
          </div>
        </div>
      </div>
      {selSession?.report && <WeldReportPanel r={selSession.report} color={module.color} isMobile={isMobile} />}
    </div>
  );
};

// ============================================================
// STUDENT MANAGER
// ============================================================
const StudentManager = ({ modules, user, onToast }) => {
  const { width: w } = useWindowSize();
  const isMobile = w < 640;
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [modal, setModal] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [errMsg, setErrMsg] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    const data = await api('/api/admin/students');
    if (data?.Status === 'SUCCESS') setStudents(data.students || []);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = students.filter(s =>
    s.name.toLowerCase().includes(search.toLowerCase()) ||
    s.email.toLowerCase().includes(search.toLowerCase())
  );
  const active = students.filter(s => s.active).length;

  const openAdd = () => { setForm({ name: '', email: '', password: '', newPassword: '', subscribed_modules: [] }); setErrMsg(''); setModal('add'); };
  const openEdit = (s) => { setForm({ ...s, newPassword: '' }); setErrMsg(''); setModal('edit'); };
  const save = async () => {
    if (!form.name || !form.email || (modal === 'add' && !form.password)) {
      setErrMsg('Fill all required fields'); return;
    }
    // ✅ NEW — block save if no modules selected
    if ((form.subscribed_modules || []).length === 0) {
      setErrMsg('Please select at least one module for this student');
      return;
    }

    setSaving(true);
    const payload = { ...form, admin_id: user.id };
    if (modal === 'edit' && form.newPassword) payload.password = form.newPassword;
    delete payload.newPassword;
    const res = await api(
      modal === 'add' ? '/api/admin/students' : `/api/admin/students/${form.id}`,
      modal === 'add' ? 'POST' : 'PUT', payload
    );
    if (res?.Status === 'SUCCESS') { onToast(modal === 'add' ? 'Student added' : 'Student updated', 'ok'); setModal(null); load(); }
    else setErrMsg(res?.Error || 'Save failed');
    setSaving(false);
  };

  const remove = async (id) => {
    const res = await api(`/api/admin/students/${id}`, 'DELETE', { admin_id: user.id });
    if (res?.Status === 'SUCCESS') { onToast('Student removed', 'ok'); load(); }
    else onToast(res?.Error || 'Delete failed', 'err');
    setConfirm(null);
  };

  const toggle = async (id) => {
    setStudents(prev => prev.map(s => s.id === id ? { ...s, active: !s.active } : s));
    const res = await api('/api/admin/toggle-access', 'POST', { user_id: id, admin_id: user.id });
    if (res?.Status === 'SUCCESS') { onToast(`Access ${res.active ? 'enabled' : 'disabled'}`, 'ok'); load(); }
    else { load(); onToast('Toggle failed', 'err'); }
  };

  const toggleMod = (mid) => {
    const mods = form.subscribed_modules || [];
    setForm(f => ({ ...f, subscribed_modules: mods.includes(mid) ? mods.filter(x => x !== mid) : [...mods, mid] }));
  };

  return (
    <div>
      {confirm && <Confirm text={`Remove "${confirm.name}"? This cannot be undone.`} onConfirm={() => remove(confirm.id)} onCancel={() => setConfirm(null)} />}

      {modal && (
        <Modal title={modal === 'add' ? 'Add Student' : 'Edit Student'} onClose={() => setModal(null)} width={460}>
          <Field label="Full Name *"><Inp value={form.name} onChange={v => setForm(f => ({ ...f, name: v }))} placeholder="e.g. John Cadet" /></Field>
          <Field label="Email *"><Inp value={form.email} onChange={v => setForm(f => ({ ...f, email: v }))} type="email" placeholder="e.g. john@himt.com" /></Field>
          {modal === 'add' && <Field label="Password *"><Inp value={form.password} onChange={v => setForm(f => ({ ...f, password: v }))} type="password" placeholder="Set password" /></Field>}
          {modal === 'edit' && <Field label="New Password (leave blank to keep current)"><Inp value={form.newPassword} onChange={v => setForm(f => ({ ...f, newPassword: v }))} type="password" placeholder="Enter new password or leave blank" /></Field>}
          <Field label="Module Access">
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 7 }}>
              {modules.map(m => {
                const has = (form.subscribed_modules || []).includes(m.id);
                return (
                  <label key={m.id} onClick={() => toggleMod(m.id)}
                    style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', background: has ? `${m.color}18` : 'rgba(30,41,59,0.5)', borderRadius: 9, cursor: 'pointer', border: `1px solid ${has ? m.color + '55' : C.border}`, transition: 'all 0.2s', minHeight: 44 }}>
                    <input type="checkbox" checked={has} onChange={() => { }} style={{ accentColor: m.color, flexShrink: 0, width: 16, height: 16 }} />
                    <span style={{ fontSize: 15 }}>{m.icon}</span>
                    <span style={{ fontSize: '0.82rem', color: has ? '#fff' : C.muted, fontWeight: has ? 600 : 400 }}>{m.name}</span>
                  </label>
                );
              })}
            </div>
          </Field>
          {errMsg && <div style={{ color: C.red, fontSize: '0.82rem', marginTop: 8, padding: '8px 12px', background: 'rgba(239,68,68,0.1)', borderRadius: 8 }}>{errMsg}</div>}
          <SaveBtn onClick={save} loading={saving} label={modal === 'add' ? 'Add Student' : 'Save Changes'} />
        </Modal>
      )}

      {/* Toolbar */}
      <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', alignItems: isMobile ? 'stretch' : 'center', marginBottom: 14, gap: 10 }}>
        <span style={{ fontSize: '0.82rem', color: C.muted }}>{students.length} students ({active} active)</span>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', flex: isMobile ? 1 : 'none' }}>
            <Search size={14} style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', color: C.dim }} />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search..."
              style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, color: '#fff', padding: '10px 11px 10px 34px', fontSize: '16px', outline: 'none', width: isMobile ? '100%' : 180, minHeight: 44 }}
              onFocus={e => e.target.style.borderColor = C.blue}
              onBlur={e => e.target.style.borderColor = C.border} />
          </div>
          <button onClick={load} style={{ background: 'rgba(74,144,226,0.1)', border: `1px solid ${C.border}`, borderRadius: 10, color: C.blue, padding: '10px 13px', cursor: 'pointer', display: 'flex', alignItems: 'center', minHeight: 44 }}><RefreshCw size={14} /></button>
          <button onClick={openAdd} style={{ background: C.blue, border: 'none', borderRadius: 10, color: '#fff', padding: '10px 18px', fontWeight: 700, fontSize: '0.88rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, flex: isMobile ? 1 : 'none', justifyContent: 'center', minHeight: 44 }}>
            <Plus size={15} /> Add Student
          </button>
        </div>
      </div>

      {/* Cards on mobile, Table on desktop */}
      {isMobile ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {loading ? <Spinner /> : filtered.length === 0
            ? <div style={{ padding: '3rem', textAlign: 'center', color: C.dim }}>{search ? 'No match' : 'No students yet'}</div>
            : filtered.map(s => (
              <div key={s.id} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: '14px 16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                  <div>
                    <div style={{ fontSize: '0.95rem', fontWeight: 700, color: '#fff' }}>{s.name}</div>
                    <div style={{ fontSize: '0.75rem', color: C.muted, marginTop: 2 }}>{s.email}</div>
                  </div>
                  <span style={{ fontSize: '0.68rem', padding: '3px 10px', borderRadius: 8, fontWeight: 700, background: s.active ? 'rgba(16,185,129,0.15)' : 'rgba(239,68,68,0.15)', color: s.active ? C.green : C.red, flexShrink: 0 }}>
                    {s.active ? 'Active' : 'Disabled'}
                  </span>
                </div>
                <div style={{ fontSize: '0.75rem', color: C.blue, marginBottom: 12 }}>{s.subscribed_modules?.length || 0} modules</div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => toggle(s.id)}
                    style={{ flex: 1, padding: '10px', fontSize: '0.82rem', borderRadius: 9, border: 'none', cursor: 'pointer', fontWeight: 600, background: s.active ? 'rgba(239,68,68,0.15)' : 'rgba(16,185,129,0.15)', color: s.active ? C.red : C.green, minHeight: 44 }}>
                    {s.active ? 'Disable' : 'Enable'}
                  </button>
                  <button onClick={() => openEdit(s)} style={{ padding: '10px 16px', background: 'rgba(74,144,226,0.15)', color: C.blue, border: 'none', borderRadius: 9, cursor: 'pointer', display: 'flex', alignItems: 'center', minHeight: 44 }}><Pencil size={15} /></button>
                  <button onClick={() => setConfirm(s)} style={{ padding: '10px 16px', background: 'rgba(239,68,68,0.15)', color: C.red, border: 'none', borderRadius: 9, cursor: 'pointer', display: 'flex', alignItems: 'center', minHeight: 44 }}><Trash2 size={15} /></button>
                </div>
              </div>
            ))
          }
        </div>
      ) : (
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, overflow: 'hidden' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1.8fr 1.8fr 1fr 0.8fr 1.6fr', gap: 8, padding: '10px 16px', background: 'rgba(15,23,42,0.85)', borderBottom: `1px solid ${C.border}` }}>
            {['Name', 'Email', 'Modules', 'Status', 'Actions'].map(h => (
              <div key={h} style={{ fontSize: '0.68rem', color: C.blue, fontWeight: 700, letterSpacing: '0.8px', textTransform: 'uppercase' }}>{h}</div>
            ))}
          </div>
          {loading ? <Spinner /> : filtered.length === 0
            ? <div style={{ padding: '3rem', textAlign: 'center', color: C.dim }}>{search ? 'No match' : 'No students yet — add one above'}</div>
            : filtered.map((s, idx) => (
              <div key={s.id}
                style={{ display: 'grid', gridTemplateColumns: '1.8fr 1.8fr 1fr 0.8fr 1.6fr', gap: 8, padding: '12px 16px', borderBottom: idx < filtered.length - 1 ? `1px solid rgba(74,144,226,0.07)` : 'none', alignItems: 'center', transition: 'background 0.15s' }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(74,144,226,0.05)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                <div><div style={{ fontSize: '0.84rem', fontWeight: 600, color: '#fff' }}>{s.name}</div><div style={{ fontSize: '0.68rem', color: C.dim }}>ID: {s.id}</div></div>
                <div style={{ fontSize: '0.78rem', color: C.muted, wordBreak: 'break-all' }}>{s.email}</div>
                <div><span style={{ fontSize: '0.72rem', padding: '3px 9px', borderRadius: 6, background: 'rgba(74,144,226,0.15)', color: C.blue, fontWeight: 700 }}>{s.subscribed_modules?.length || 0} modules</span></div>
                <div><span style={{ fontSize: '0.68rem', padding: '3px 9px', borderRadius: 8, fontWeight: 700, background: s.active ? 'rgba(16,185,129,0.15)' : 'rgba(239,68,68,0.15)', color: s.active ? C.green : C.red }}>{s.active ? 'Active' : 'Disabled'}</span></div>
                <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
                  <button onClick={() => toggle(s.id)} style={{ fontSize: '0.68rem', padding: '5px 9px', borderRadius: 6, border: 'none', cursor: 'pointer', fontWeight: 600, whiteSpace: 'nowrap', background: s.active ? 'rgba(239,68,68,0.15)' : 'rgba(16,185,129,0.15)', color: s.active ? C.red : C.green }}>{s.active ? 'Disable' : 'Enable'}</button>
                  <button onClick={() => openEdit(s)} style={{ background: 'rgba(74,144,226,0.15)', color: C.blue, border: 'none', borderRadius: 6, padding: '5px 8px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}><Pencil size={12} /></button>
                  <button onClick={() => setConfirm(s)} style={{ background: 'rgba(239,68,68,0.15)', color: C.red, border: 'none', borderRadius: 6, padding: '5px 8px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}><Trash2 size={12} /></button>
                </div>
              </div>
            ))
          }
        </div>
      )}
    </div>
  );
};

// ============================================================
// MODULE MANAGER
// ============================================================
const ModuleManager = ({ user, onToast, onModulesChanged }) => {
  const { width: w } = useWindowSize();
  const isMobile = w < 640;
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [form, setForm] = useState({});
  const [taskInput, setTaskInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [errMsg, setErrMsg] = useState('');
  const [imgMode, setImgMode] = useState('icon'); // 'icon' | 'image' — which card-header style is active in the form
  const [imgUploading, setImgUploading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const data = await api('/api/admin/modules');
    if (data?.Status === 'SUCCESS') { setModules(data.modules || []); onModulesChanged(data.modules || []); }
    setLoading(false);
  }, [onModulesChanged]);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => { setForm({ name: '', icon: '⚙️', image: '', color: '#3b82f6', tasks: [], hasGuided: true, hasAssessment: false }); setTaskInput(''); setErrMsg(''); setImgMode('icon'); setModal('add'); };
  const openEdit = (m) => { setForm({ ...m, tasks: [...(m.tasks || [])] }); setTaskInput(''); setErrMsg(''); setImgMode(m.image ? 'image' : 'icon'); setModal('edit'); };

  // Convert an uploaded image file to a base64 data URL for storage on the module record.
  // Uploading a photo always switches the card header to Image mode automatically —
  // no need to tap the "Image" tab first.
  const handleImageFile = (file) => {
    if (!file) return;
    setImgUploading(true);
    const reader = new FileReader();
    reader.onload = () => { setForm(f => ({ ...f, image: reader.result })); setImgMode('image'); setImgUploading(false); };
    reader.onerror = () => { setErrMsg('Could not read that image file'); setImgUploading(false); };
    reader.readAsDataURL(file);
  };

  const save = async () => {
    if (!form.name) { setErrMsg('Module name required'); return; }
    setSaving(true);
    const isAdd = modal === 'add';
    const res = await api(isAdd ? '/api/admin/modules' : `/api/admin/modules/${form.id}`, isAdd ? 'POST' : 'PUT', { ...form, admin_id: user.id });
    if (res?.Status === 'SUCCESS') { onToast(isAdd ? 'Module added' : 'Module updated', 'ok'); setModal(null); load(); }
    else setErrMsg(res?.Error || 'Save failed');
    setSaving(false);
  };

  const remove = async (id) => {
    const res = await api(`/api/admin/modules/${id}`, 'DELETE', { admin_id: user.id });
    if (res?.Status === 'SUCCESS') { onToast('Module deleted', 'ok'); load(); }
    else onToast(res?.Error || 'Delete failed', 'err');
    setConfirm(null);
  };

  const addTask = () => { const t = taskInput.trim(); if (!t || (form.tasks || []).includes(t)) return; setForm(f => ({ ...f, tasks: [...(f.tasks || []), t] })); setTaskInput(''); };
  const removeTask = (t) => setForm(f => ({ ...f, tasks: f.tasks.filter(x => x !== t) }));

  return (
    <div>
      {confirm && <Confirm text={`Delete "${confirm.name}"? Students will lose access.`} onConfirm={() => remove(confirm.id)} onCancel={() => setConfirm(null)} />}
      {modal && (
        <Modal title={modal === 'add' ? 'Add Module' : 'Edit Module'} onClose={() => setModal(null)} width={470}>
          <Field label="Module Name *"><Inp value={form.name} onChange={v => setForm(f => ({ ...f, name: v }))} placeholder="e.g. Engine Control Room" /></Field>
          <Field label="Card Header">
            {/* Icon / Image toggle — pick one style for the module card */}
            <div style={{ display: 'flex', gap: 6, marginBottom: 10, background: 'rgba(15,23,42,0.6)', padding: 4, borderRadius: 10, border: `1px solid ${C.border}` }}>
              {[['icon', 'Icon'], ['image', 'Image']].map(([mode, label]) => (
                <button key={mode} onClick={() => { setImgMode(mode); if (mode === 'icon') setForm(f => ({ ...f, image: '' })); }}
                  style={{ flex: 1, padding: '9px 0', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 700, minHeight: 40, background: imgMode === mode ? C.blue : 'transparent', color: imgMode === mode ? '#fff' : C.muted, transition: 'all 0.2s' }}>
                  {label}
                </button>
              ))}
            </div>

            {imgMode === 'icon' ? (
              <div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7, marginBottom: 10 }}>
                  {ICON_OPTIONS.map(ic => (
                    <button key={ic} onClick={() => setForm(f => ({ ...f, icon: ic }))}
                      style={{ width: 42, height: 42, fontSize: 20, borderRadius: 10, border: form.icon === ic ? `2px solid ${C.blue}` : `1px solid ${C.border}`, background: form.icon === ic ? 'rgba(59,130,246,0.18)' : C.card, cursor: 'pointer' }}>{ic}</button>
                  ))}
                </div>
                {/* Uploading here switches straight to Image mode — no need to tap the Image tab first */}
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 14px', background: 'rgba(74,144,226,0.15)', border: `1px solid ${C.border}`, borderRadius: 10, color: C.blue, cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600, minHeight: 40 }}>
                  {imgUploading ? 'Loading…' : 'Or upload a photo instead'}
                  <input type="file" accept="image/*" onChange={e => handleImageFile(e.target.files?.[0])} style={{ display: 'none' }} />
                </label>
              </div>
            ) : (
              <div>
                {form.image && (
                  <div style={{ position: 'relative', marginBottom: 10, borderRadius: 10, overflow: 'hidden', border: `1px solid ${C.border}`, height: 100 }}>
                    <img src={form.image} alt="preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    <button onClick={() => setForm(f => ({ ...f, image: '' }))}
                      style={{ position: 'absolute', top: 6, right: 6, width: 26, height: 26, borderRadius: 7, border: 'none', background: 'rgba(10,22,40,0.85)', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <X size={13} />
                    </button>
                  </div>
                )}
                <div style={{ display: 'flex', gap: 7, marginBottom: 8 }}>
                  <div style={{ flex: 1 }}>
                    <Inp value={form.image && form.image.startsWith('data:') ? '' : form.image} onChange={v => setForm(f => ({ ...f, image: v }))} placeholder="Paste image URL…" />
                  </div>
                  <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 16px', background: 'rgba(74,144,226,0.15)', border: `1px solid ${C.border}`, borderRadius: 10, color: C.blue, cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600, whiteSpace: 'nowrap', minHeight: 44 }}>
                    {imgUploading ? 'Loading…' : 'Upload'}
                    <input type="file" accept="image/*" onChange={e => handleImageFile(e.target.files?.[0])} style={{ display: 'none' }} />
                  </label>
                </div>
                <div style={{ fontSize: '0.72rem', color: C.dim }}>Paste a link or upload a photo. This image replaces the icon on the module card.</div>
              </div>
            )}
          </Field>
          <Field label="Color">
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
              {COLOR_OPTIONS.map(c => (
                <button key={c} onClick={() => setForm(f => ({ ...f, color: c }))}
                  style={{ width: 34, height: 34, borderRadius: 8, background: c, border: form.color === c ? '3px solid #fff' : '2px solid transparent', cursor: 'pointer' }} />
              ))}
            </div>
          </Field>
          <Field label="Features">
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              {[['hasGuided', 'Training'], ['hasAssessment', 'Assessment']].map(([k, l]) => (
                <label key={k} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: '0.88rem', color: '#ddd', padding: '10px 14px', background: 'rgba(30,41,59,0.5)', borderRadius: 10, border: `1px solid ${form[k] ? C.blue : C.border}`, flex: 1, minHeight: 44 }}>
                  <input type="checkbox" checked={!!form[k]} onChange={e => setForm(f => ({ ...f, [k]: e.target.checked }))} style={{ accentColor: C.blue, width: 16, height: 16 }} />{l}
                </label>
              ))}
            </div>
          </Field>
          <Field label="Tasks">
            <div style={{ display: 'flex', gap: 7, marginBottom: 9 }}>
              <input value={taskInput} onChange={e => setTaskInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && addTask()} placeholder="Task name — press Enter or Add"
                style={{ flex: 1, background: 'rgba(15,23,42,0.8)', border: `1px solid ${C.border}`, borderRadius: 10, color: '#fff', padding: '10px 12px', fontSize: '16px', outline: 'none', minHeight: 44 }}
                onFocus={e => e.target.style.borderColor = C.blue}
                onBlur={e => e.target.style.borderColor = C.border} />
              <button onClick={addTask} style={{ background: C.blue, border: 'none', borderRadius: 10, color: '#fff', padding: '10px 16px', cursor: 'pointer', fontWeight: 700, fontSize: '0.88rem', minHeight: 44 }}>Add</button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 180, overflowY: 'auto' }}>
              {(form.tasks || []).map((t, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '9px 12px', background: 'rgba(30,41,59,0.5)', borderRadius: 9, border: `1px solid ${C.border}`, minHeight: 42 }}>
                  <span style={{ fontSize: '0.82rem', color: '#ddd' }}>{i + 1}. {t}</span>
                  <button onClick={() => removeTask(t)} style={{ background: 'none', border: 'none', color: C.dim, cursor: 'pointer', padding: 6, display: 'flex', alignItems: 'center' }}><X size={13} /></button>
                </div>
              ))}
              {!form.tasks?.length && <div style={{ fontSize: '0.75rem', color: C.dim }}>No tasks added yet</div>}
            </div>
          </Field>
          {errMsg && <div style={{ color: C.red, fontSize: '0.82rem', marginTop: 8, padding: '8px 12px', background: 'rgba(239,68,68,0.1)', borderRadius: 8 }}>{errMsg}</div>}
          <SaveBtn onClick={save} loading={saving} label={modal === 'add' ? 'Add Module' : 'Save Changes'} />
        </Modal>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, gap: 10, flexWrap: 'wrap' }}>
        <span style={{ fontSize: '0.82rem', color: C.muted }}>{modules.length} active modules</span>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={load} style={{ background: 'rgba(74,144,226,0.1)', border: `1px solid ${C.border}`, borderRadius: 10, color: C.blue, padding: '10px 13px', cursor: 'pointer', display: 'flex', alignItems: 'center', minHeight: 44 }}><RefreshCw size={14} /></button>
          <button onClick={openAdd} style={{ background: C.blue, border: 'none', borderRadius: 10, color: '#fff', padding: '10px 18px', fontWeight: 700, fontSize: '0.88rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, minHeight: 44 }}>
            <Plus size={15} /> Add Module
          </button>
        </div>
      </div>

      {/* Cards on mobile, Table on desktop */}
      {isMobile ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {loading ? <Spinner /> : modules.length === 0
            ? <div style={{ padding: '3rem', textAlign: 'center', color: C.dim }}>No modules yet</div>
            : modules.map(m => (
              <div key={m.id} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: '14px 16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
                  <div style={{ width: 72, height: 48, borderRadius: 10, background: `${m.color}35`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0, overflow: 'hidden' }}>
                    {m.image ? <img src={m.image} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} /> : m.icon}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '0.9rem', fontWeight: 700, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.name}</div>
                    <div style={{ fontSize: '0.72rem', color: C.dim }}>{m.tasks?.length || 0} tasks • {m.progress || 0}% done</div>
                  </div>
                </div>
                <div style={{ background: 'rgba(15,23,42,0.8)', borderRadius: 999, height: 4, overflow: 'hidden', marginBottom: 12 }}>
                  <div style={{ background: m.color, height: '100%', width: `${m.progress || 0}%`, borderRadius: 999 }} />
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => openEdit(m)} style={{ flex: 1, padding: '10px', background: 'rgba(74,144,226,0.15)', color: C.blue, border: 'none', borderRadius: 9, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, fontSize: '0.82rem', fontWeight: 600, minHeight: 44 }}><Pencil size={13} /> Edit</button>
                  <button onClick={() => setConfirm(m)} style={{ flex: 1, padding: '10px', background: 'rgba(239,68,68,0.15)', color: C.red, border: 'none', borderRadius: 9, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, fontSize: '0.82rem', fontWeight: 600, minHeight: 44 }}><Trash2 size={13} /> Delete</button>
                </div>
              </div>
            ))
          }
        </div>
      ) : (
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, overflow: 'hidden' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 2fr 1fr 1fr', gap: 8, padding: '10px 16px', background: 'rgba(15,23,42,0.85)', borderBottom: `1px solid ${C.border}` }}>
            {['Module', 'Tasks', 'Features', 'Progress', 'Actions'].map(h => (
              <div key={h} style={{ fontSize: '0.68rem', color: C.blue, fontWeight: 700, letterSpacing: '0.8px', textTransform: 'uppercase' }}>{h}</div>
            ))}
          </div>
          {loading ? <Spinner /> : modules.length === 0
            ? <div style={{ padding: '3rem', textAlign: 'center', color: C.dim }}>No modules — add one above</div>
            : modules.map((m, idx) => (
              <div key={m.id}
                style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 2fr 1fr 1fr', gap: 8, padding: '12px 16px', borderBottom: idx < modules.length - 1 ? `1px solid rgba(74,144,226,0.07)` : 'none', alignItems: 'center', transition: 'background 0.15s' }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(74,144,226,0.05)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 58, height: 40, borderRadius: 9, background: `${m.color}35`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 17, flexShrink: 0, overflow: 'hidden' }}>
                    {m.image ? <img src={m.image} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} /> : m.icon}
                  </div>
                  <div><div style={{ fontSize: '0.84rem', fontWeight: 600, color: '#fff' }}>{m.name}</div><div style={{ fontSize: '0.68rem', color: C.dim }}>ID: {m.id}</div></div>
                </div>
                <div style={{ fontSize: '0.78rem', color: C.muted }}>{m.tasks?.length || 0}</div>
                <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                  {m.hasGuided && <span style={{ fontSize: '0.65rem', padding: '3px 8px', borderRadius: 5, background: `${m.color}20`, color: m.color, fontWeight: 700, border: `1px solid ${m.color}40` }}>Training</span>}
                  {m.hasAssessment && <span style={{ fontSize: '0.65rem', padding: '3px 8px', borderRadius: 5, background: `${m.color}20`, color: m.color, fontWeight: 700, border: `1px solid ${m.color}40` }}>Assessment</span>}
                </div>
                <div>
                  <div style={{ background: 'rgba(15,23,42,0.8)', borderRadius: 999, height: 5, overflow: 'hidden', marginBottom: 2 }}>
                    <div style={{ background: m.color, height: '100%', width: `${m.progress || 0}%`, borderRadius: 999 }} />
                  </div>
                  <div style={{ fontSize: '0.68rem', color: C.muted }}>{m.progress || 0}%</div>
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button onClick={() => openEdit(m)} style={{ background: 'rgba(74,144,226,0.15)', color: C.blue, border: 'none', borderRadius: 7, padding: '5px 9px', cursor: 'pointer' }}><Pencil size={12} /></button>
                  <button onClick={() => setConfirm(m)} style={{ background: 'rgba(239,68,68,0.15)', color: C.red, border: 'none', borderRadius: 7, padding: '5px 9px', cursor: 'pointer' }}><Trash2 size={12} /></button>
                </div>
              </div>
            ))
          }
        </div>
      )}
    </div>
  );
};

// ============================================================
// MAIN DASHBOARD
// ============================================================
// Local / Server data source switch
const SHOW_SOURCE_SWITCH = false; // set true to show the Local / Server buttons again
const SourceSwitch = ({ src, onChange, fixed }) => !SHOW_SOURCE_SWITCH ? null : (
  <div style={{ display: 'flex', gap: 4, background: 'rgba(15,23,42,0.8)', border: `1px solid ${C.border}`, borderRadius: 9, padding: 3, ...(fixed ? { position: 'fixed', top: 12, right: 12, zIndex: 200 } : {}) }}>
    {[['local', 'Local'], ['server', 'Server']].map(([id, label]) => {
      const off = id === 'local' && IS_HTTPS;
      return (
        <button key={id} disabled={off} onClick={() => onChange(id)}
          title={off ? 'Local DB works from localhost:45 or the LAN dashboard (this page is https)' : (id === 'local' ? LOCAL_URL : (SERVER_URL || window.location.origin))}
          style={{ padding: '5px 12px', borderRadius: 7, border: 'none', fontSize: '0.72rem', fontWeight: 700, cursor: off ? 'not-allowed' : 'pointer', opacity: off ? 0.4 : 1, background: src === id ? C.blue : 'transparent', color: '#fff' }}>
          {label}
        </button>
      );
    })}
  </div>
);

const Dashboard = () => {
  const { width: w } = useWindowSize();
  const isMobile = w < 640;
  const isAdminRoute = window.location.pathname.startsWith('/admin');

  const [user, setUser] = useState(null);
  const [view, setView] = useState('modules');
  const [adminTab, setAdminTab] = useState('students');
  const [selMod, setSelMod] = useState(null);
  const [modules, setModules] = useState([]);
  const [modsLoading, setMLoading] = useState(false);
  const [students, setStudents] = useState([]);
  const [toast, setToast] = useState(null);
  const [src, setSrc] = useState(currentSource());

  const showToast = (msg, type = 'ok') => { setToast({ msg, type }); setTimeout(() => setToast(null), 3500); };
  const isAdmin = user?.role === 'admin';
  const visibleMods = isAdmin ? modules : modules.filter(m => (user?.subscribed_modules || []).includes(m.id));

  // Student cards: progress is computed from THIS user's own sessions
  // (same /api/vr/sessions endpoint the Analytics page uses), so clearing
  // the user's data resets the cards too. Admin cards are unchanged.
  const withUserProgress = async (mods, uid) => Promise.all(mods.map(async m => {
    const tasks = (m.tasks || []).map(t => typeof t === 'string' ? t : t?.name).filter(Boolean);
    const total = tasks.length;
    let done = 0;
    try {
      const d = await api(`/api/vr/sessions/${uid}/${m.id}`);
      const steps = d?.Status === 'SUCCESS'
        ? (d.sessions || []).flatMap(s => s.steps || []).filter(st => st.performed)
        : [];
      const ui = getModuleUI(m.id);
      if (total === 1) done = steps.length > 0 ? 1 : 0;
      else done = tasks.filter(t => steps.some(st => sameTask(ui, st.task_name, t))).length;
    } catch { done = 0; }
    return { ...m, completedTasks: done, totalTasks: total, progress: total ? Math.round((done / total) * 100) : 0 };
  }));

  const loadModules = useCallback(async () => {
    setMLoading(true);
    const data = await api('/api/admin/modules');
    if (data?.Status === 'SUCCESS') {
      let mods = data.modules || [];
      if (user && user.role !== 'admin') {
        const subs = user.subscribed_modules || [];
        mods = await withUserProgress(mods.filter(m => subs.includes(m.id)), user.id);
      }
      setModules(mods);
    }
    setMLoading(false);
  }, [user]);

  const loadStudents = useCallback(async () => {
    if (!isAdmin) return;
    const data = await api('/api/admin/students');
    if (data?.Status === 'SUCCESS') setStudents(data.students || []);
  }, [isAdmin]);

  useEffect(() => { if (user) { loadModules(); loadStudents(); } }, [user, loadModules, loadStudents]);
  const changeSource = (s) => {
    if (s === src) return;
    setApiSource(s); setSrc(s);
    setSelMod(null); setView('modules'); setModules([]); setStudents([]);
    if (user) { loadModules(); loadStudents(); }
  };

  if (!user && isAdminRoute) return <><GlobalStyles /><SourceSwitch src={src} onChange={changeSource} fixed /><AdminLoginPage onLogin={setUser} /></>;
  if (!user && !isAdminRoute) return <><GlobalStyles /><SourceSwitch src={src} onChange={changeSource} fixed /><StudentLoginPage onLogin={setUser} /></>;

  const navItems = [
    { id: 'modules', label: 'Modules', icon: '▦' },
    { id: 'analytics', label: 'Analytics', icon: '📊', disabled: !selMod },
    ...(isAdmin ? [{ id: 'admin', label: 'Admin', icon: '🛡' }] : [])
  ];

  return (
    <div style={{ minHeight: '100vh', minHeight: '100dvh', background: `linear-gradient(135deg,${C.bg},#1a1f3a)` }}>
      <GlobalStyles />
      {toast && <Toast msg={toast.msg} type={toast.type} />}

      {/* Header */}
      <header style={{ background: C.surface, borderBottom: `1px solid ${C.border}`, padding: isMobile ? '10px 14px' : '12px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, zIndex: 100, paddingTop: `calc(${isMobile ? '10px' : '12px'} + env(safe-area-inset-top,0px))` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <img src={KITBIT_LOGO} alt="KITBIT" style={{ width: isMobile ? 34 : 40, height: isMobile ? 34 : 40, objectFit: 'contain', borderRadius: 8 }} />
          <div>
            <div style={{ fontSize: isMobile ? '0.95rem' : '1.08rem', fontWeight: 800, color: '#fff', letterSpacing: 2.5 }}>KITBIT</div>
            {!isMobile && <div style={{ fontSize: '0.6rem', color: C.dim, letterSpacing: 2.5 }}>DIGITAL SOLUTIONS</div>}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <SourceSwitch src={src} onChange={changeSource} />
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: isMobile ? '0.8rem' : '0.86rem', fontWeight: 600, color: '#fff' }}>{isMobile ? user.name.split(' ')[0] : user.name}</div>
            {!isMobile && <div style={{ fontSize: '0.68rem', color: C.dim }}>{user.email}</div>}
            <span style={{ fontSize: '0.6rem', padding: '2px 8px', borderRadius: 10, background: isAdmin ? 'rgba(74,144,226,0.2)' : 'rgba(16,185,129,0.2)', color: isAdmin ? C.blue : C.green, fontWeight: 700 }}>
              {isAdmin ? 'ADMIN' : 'STUDENT'}
            </span>
          </div>
          <button onClick={() => { setUser(null); setView('modules'); setSelMod(null); setModules([]); }}
            style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', color: C.red, cursor: 'pointer', padding: '9px', borderRadius: 9, display: 'flex', minHeight: 40, minWidth: 40, alignItems: 'center', justifyContent: 'center' }}>
            <LogOut size={17} />
          </button>
        </div>
      </header>

      {/* Bottom Nav on mobile, Top Nav on desktop */}
      {isMobile ? (
        <nav style={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 100, background: C.surface, borderTop: `1px solid ${C.border}`, display: 'flex', paddingBottom: 'env(safe-area-inset-bottom,0px)' }}>
          {navItems.map(t => (
            <button key={t.id} disabled={t.disabled} onClick={() => !t.disabled && setView(t.id)}
              style={{ flex: 1, padding: '10px 6px 8px', background: 'none', border: 'none', color: view === t.id ? C.blue : C.dim, cursor: t.disabled ? 'not-allowed' : 'pointer', opacity: t.disabled ? 0.35 : 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, fontSize: '0.6rem', fontWeight: 700, transition: 'color 0.2s', minHeight: 54 }}>
              <span style={{ fontSize: 18 }}>{t.icon}</span>
              {t.label}
            </button>
          ))}
        </nav>
      ) : (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 9, padding: '14px 24px 8px', maxWidth: 1400, margin: '0 auto', flexWrap: 'wrap' }}>
          {navItems.map(t => (
            <button key={t.id} disabled={t.disabled} onClick={() => !t.disabled && setView(t.id)}
              style={{ padding: '9px 24px', borderRadius: 11, fontSize: '0.86rem', fontWeight: 600, cursor: t.disabled ? 'not-allowed' : 'pointer', border: 'none', background: view === t.id ? C.blue : 'rgba(30,41,59,0.6)', color: '#fff', opacity: t.disabled ? 0.35 : 1, display: 'flex', alignItems: 'center', gap: 7, transition: 'all 0.2s', boxShadow: view === t.id ? `0 4px 16px ${C.blue}50` : 'none' }}>
              <span style={{ fontSize: 14 }}>{t.icon}</span>{t.label}
            </button>
          ))}
        </div>
      )}

      {/* Main — extra bottom padding on mobile for the bottom nav */}
      <main style={{ padding: isMobile ? '8px 14px 80px' : '8px 24px 40px', maxWidth: 1400, margin: '0 auto' }}>
        {view === 'modules' && (
          <ModulesView modules={visibleMods} loading={modsLoading} isAdmin={isAdmin} onRefresh={loadModules}
            onSelect={m => { setSelMod(m); setView('analytics'); }} />
        )}
        {view === 'analytics' && selMod && (
          <AnalyticsView module={selMod} user={user} isAdmin={isAdmin} allStudents={students} />
        )}
        {view === 'admin' && isAdmin && (
          <div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 18 }}>
              {[['students', '👥 Students'], ['modules', '🗂 Modules']].map(([id, label]) => (
                <button key={id} onClick={() => setAdminTab(id)}
                  style={{ flex: isMobile ? 1 : 'none', padding: '10px 22px', borderRadius: 11, fontSize: '0.86rem', fontWeight: 600, cursor: 'pointer', border: 'none', background: adminTab === id ? C.blue : 'rgba(30,41,59,0.6)', color: '#fff', transition: 'all 0.2s', minHeight: 44 }}>{label}</button>
              ))}
            </div>
            {adminTab === 'students' && <StudentManager modules={modules} user={user} onToast={showToast} />}
            {adminTab === 'modules' && <ModuleManager user={user} onToast={showToast} onModulesChanged={setModules} />}
          </div>
        )}
      </main>
    </div>
  );
};

export default Dashboard;